import type { GridColDef, GridRowScrollEndParams, GridSortModel } from '@mui/x-data-grid-pro';
import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { User } from '@/scenes/authentication/types/types.auth';
import { Edit } from '@mui/icons-material';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import SendEmailModal from '@shared/components/SendEmailModal';
import CustomSnackbar from '@shared/components/Snackbar';
import { StyledActionButton } from '@shared/components/Tables/Table';
import { UserContext } from '@/shared/contexts/UserContext';
import { StyledAutocomplete, TableHeaderWrapper } from '../styles/admin.styles';
import AdminEditUserModal from './AdminEditUserModal';
import { log } from '../../utils/log';

interface AdminUserTableProps {
  tabChange: string;
}

// User data interface for DataGrid rows
interface UserTableRow {
  id: string | number;
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  subscriptionPlan: string;
  createdAt: string;
  referer: string;
  source: string;
  campaign: string;
  rawUser: User;
}

function extractUserInfo(user: User): UserTableRow {
  const { firstName, lastName, accountMemberships, email, createdAt, referer, source, campaign } = user;

  return {
    id: user.id || email, // Use email as fallback ID
    firstName: firstName || '-',
    lastName: lastName || '-',
    company: accountMemberships![0]?.account?.name || '-',
    email,
    subscriptionPlan: _.capitalize(accountMemberships![0]?.account!.subscriptions![0]?.planSlug ?? 'N/A'),
    createdAt: dayjs(createdAt).format('YYYY-MM-DD'),
    referer: referer || '-',
    source: source || '-',
    campaign: campaign || '-',
    rawUser: user,
  };
}

function AdminUserTable(props: AdminUserTableProps) {
  const { tabChange } = props;
  const [loading, setLoading] = useState(false);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const [openInviteNewUserModal, setOpenInviteNewUserModal] = useState(false);
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userTableData, setUserTableData] = useState<UserTableRow[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);

  // Derive orderBy and orderDirection from sortModel
  const orderBy = sortModel[0]?.field || 'createdAt';
  const orderDirection = (sortModel[0]?.sort || 'desc').toUpperCase();

  // Infinite scrolling states
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageUsers, setNextPageUsers] = useState<User[]>([]);
  const PAGE_SIZE = 100;

  const { user } = useContext(UserContext);

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  const getAllRegisteredUsers = useCallback(async () => {
    if (!user?.idToken) return;
    setLoading(true);
    setCurrentOffset(0);
    setHasNextPage(true);
    setNextPageUsers([]);

    try {
      const res = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/admin/user`, {
        headers: {
          Authorization: user?.idToken,
        },
        params: {
          limit: PAGE_SIZE,
          offset: 0,
          orderBy,
          orderDirection,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      });
      // Handle both old array format and new {data, meta} format
      const users = res.data.data ?? res.data.results ?? res.data;
      const tableData = users.map((userData: User) => extractUserInfo(userData));
      setUserTableData(tableData);

      // Check if there are more results
      setHasNextPage(res.data.meta?.hasMore ?? users.length === PAGE_SIZE);
    } catch (error) {
      log.error('Failed to fetch users:', error);
      setSnackbarConfig({
        message: 'An error has occurred, please try again later',
        type: 'error',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.idToken, orderBy, orderDirection, debouncedSearch]);

  // Load more users for infinite scroll
  const loadMoreUsers = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || !user?.idToken) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const nextOffset = currentOffset + PAGE_SIZE;

      const res = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/admin/user`, {
        headers: {
          Authorization: user?.idToken,
        },
        params: {
          limit: PAGE_SIZE,
          offset: nextOffset,
          orderBy,
          orderDirection,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      });

      // Handle both old array format and new {data, meta} format
      const users = res.data.data ?? res.data.results ?? res.data;
      const tableData = users.map((userData: User) => extractUserInfo(userData));

      // Append new rows, deduplicating by ID
      setUserTableData((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const uniqueNewRows = tableData.filter((r: UserTableRow) => !existingIds.has(r.id));
        return [...prev, ...uniqueNewRows];
      });
      setCurrentOffset(nextOffset);
      setHasNextPage(res.data.meta?.hasMore ?? users.length === PAGE_SIZE);
    } catch (error) {
      log.error('Error loading more users:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasNextPage, isLoadingMore, currentOffset, PAGE_SIZE, user?.idToken, orderBy, orderDirection, debouncedSearch]);

  // Predictively load next page for prefetching
  const prefetchNextPage = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || nextPageUsers.length > 0 || !user?.idToken) {
      return;
    }

    try {
      const nextOffset = currentOffset + PAGE_SIZE;

      const res = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/admin/user`, {
        headers: {
          Authorization: user?.idToken,
        },
        params: {
          limit: PAGE_SIZE,
          offset: nextOffset,
          orderBy,
          orderDirection,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      });

      // Handle both old array format and new {data, meta} format
      const users = res.data.data ?? res.data.results ?? res.data;
      if (users) {
        setNextPageUsers(users);
      }
    } catch (error) {
      log.error('Error prefetching next page:', error);
    }
  }, [
    hasNextPage,
    isLoadingMore,
    nextPageUsers.length,
    currentOffset,
    PAGE_SIZE,
    user?.idToken,
    orderBy,
    orderDirection,
    debouncedSearch,
  ]);

  // Trigger predictive prefetching when rows load or offset changes
  useEffect(() => {
    if (userTableData.length >= PAGE_SIZE * 0.5 && hasNextPage && !isLoadingMore && nextPageUsers.length === 0) {
      const timer = setTimeout(() => prefetchNextPage(), 500);
      return () => clearTimeout(timer);
    }
  }, [
    userTableData.length,
    currentOffset,
    PAGE_SIZE,
    hasNextPage,
    isLoadingMore,
    nextPageUsers.length,
    prefetchNextPage,
  ]);

  // Handle infinite scroll
  const handleRowScrollEnd = useCallback(
    async (_params: GridRowScrollEndParams) => {
      if (!hasNextPage || isLoadingMore || loading) {
        return;
      }

      setIsLoadingMore(true);

      try {
        if (nextPageUsers.length > 0) {
          const tableData = nextPageUsers.map((userData: User) => extractUserInfo(userData));

          // Check for duplicates before appending - use IDs for deduplication
          setUserTableData((prev) => {
            const existingIds = new Set(prev.map((r) => r.id));
            const uniqueNewRows = tableData.filter((r) => !existingIds.has(r.id));
            return [...prev, ...uniqueNewRows];
          });
          setCurrentOffset((prev) => prev + PAGE_SIZE);
          setNextPageUsers([]);
          // Note: Don't call prefetchNextPage here - let the useEffect handle it
          // to avoid stale closure issues with currentOffset
        } else {
          await loadMoreUsers();
        }
      } finally {
        setIsLoadingMore(false);
      }
    },
    [hasNextPage, isLoadingMore, loading, nextPageUsers, loadMoreUsers, PAGE_SIZE],
  );

  // Reset pagination when sort or search changes
  useEffect(() => {
    setCurrentOffset(0);
    setHasNextPage(true);
    setNextPageUsers([]);
  }, [orderBy, orderDirection, debouncedSearch]);

  // Debounce the search input before sending to the server
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Handle sort model change
  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  }, []);

  const handleSaveUser = async (userData: any) => {
    try {
      await axios.put(`${import.meta.env.VITE_API_BASE_URL}/admin/user/${userData.id}`, userData, {
        headers: {
          Authorization: user?.idToken,
        },
      });

      setSnackbarConfig({
        message: 'User updated successfully',
        type: 'success',
        open: true,
      });

      setOpenEditUserModal(false);
      setSelectedUser(null);

      // Refresh the user list
      getAllRegisteredUsers();
    } catch (error) {
      log.error('Failed to update user:', error);
      setSnackbarConfig({
        message: 'Failed to update user',
        type: 'error',
        open: true,
      });
    }
  };

  useEffect(() => {
    getAllRegisteredUsers();
  }, [user?.idToken, tabChange, getAllRegisteredUsers]);

  const handleEditClick = (userRow: UserTableRow) => {
    setSelectedUser(userRow.rawUser);
    setOpenEditUserModal(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 120,
      sortable: true,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 120,
      sortable: true,
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 180,
      sortable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
      sortable: true,
    },
    {
      field: 'subscriptionPlan',
      headerName: 'Plan',
      width: 120,
      sortable: true,
    },
    {
      field: 'createdAt',
      headerName: 'Date Created',
      width: 130,
      sortable: true,
    },
    {
      field: 'referer',
      headerName: 'Referrer',
      width: 120,
      sortable: true,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 120,
      sortable: true,
    },
    {
      field: 'campaign',
      headerName: 'Campaign',
      width: 120,
      sortable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit User">
            <IconButton size="small" onClick={() => handleEditClick(params.row)} sx={{ mr: 1 }}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading || !user?.idToken) {
    return <LoadingSpinner variant="linear" size="medium" />;
  }

  return (
    <>
      <TableHeaderWrapper>
        <StyledAutocomplete
          freeSolo
          disableClearable
          options={userTableData.map((userRow: UserTableRow) => ({
            label: userRow.email,
            value: userRow.email,
          }))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search User (name or email)"
              InputProps={
                {
                  ...params.InputProps,
                  type: 'search',
                } as any
              }
              onChange={(e) => setSearchInput(e.target.value)}
            />
          )}
        />
        <StyledActionButton typeVariant="create" onClick={() => setOpenInviteNewUserModal(true)}>
          Invite New User
        </StyledActionButton>
      </TableHeaderWrapper>
      <Box sx={{ height: 'calc(100vh - 300px)', width: '100%', mt: 2 }}>
        <DataGridPro
          rows={userTableData}
          columns={columns}
          loading={loading || isLoadingMore}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onRowsScrollEnd={handleRowScrollEnd}
          scrollEndThreshold={600}
          pagination={false}
          disableRowSelectionOnClick
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: 'primary.light',
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            height: '100%',
            width: '100%',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              color: '#1976d2',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              borderBottom: '2px solid #1976d2',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none', // Hide footer since we don't need pagination controls
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              margin: 0,
            },
          }}
        />
      </Box>
      <SendEmailModal isOpen={openInviteNewUserModal} handleClose={() => setOpenInviteNewUserModal(false)} />
      <AdminEditUserModal
        isOpen={openEditUserModal}
        user={selectedUser}
        onClose={() => {
          setOpenEditUserModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
      />
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </>
  );
}

export default AdminUserTable;
